import { motion } from "framer-motion";
import { ArrowUpRight, BadgeCheck, ExternalLink } from "lucide-react";
import { PARTNERS, REGIONAL_PARTNERS } from "@/data/site";
import Reveal from "@/components/motion/Reveal";

export default function PartnerSection() {
  return (
    <section id="partner" className="relative bg-[#080808] py-24 sm:py-32" data-testid="partner-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <Reveal className="mb-14 max-w-3xl">
          <span className="inline-flex items-center gap-2 border border-volt/40 bg-volt/10 px-4 py-1.5 font-body text-[11px] font-bold uppercase tracking-[0.2em] text-volt">
            <BadgeCheck className="h-4 w-4" /> Starke Partner
          </span>
          <h2 className="mt-6 font-heading text-4xl font-black uppercase leading-[0.9] tracking-tight text-white sm:text-6xl">
            Partner & <span className="text-volt">Technik</span>
          </h2>
          <p className="mt-5 font-body text-base leading-relaxed text-neutral-400">
            KeyMaster setzt auf starke Partner: <strong className="text-neutral-200">Dr-Coding</strong> ist
            der Tuner hinter Arturs Software-Files und liefert die Basis für unser
            professionelles Chiptuning im Raum Cloppenburg. <strong className="text-neutral-200">FileSuite</strong> steckt
            hinter dieser Website und dem Fahrzeug-Konfigurator.
          </p>
        </Reveal>

        <div className="grid gap-4 md:grid-cols-2">
          {PARTNERS.map((p, i) => (
            <motion.a
              key={p.name}
              href={p.url}
              target="_blank"
              rel="noopener"
              data-testid={`partner-link-${p.name.toLowerCase()}`}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="group relative flex flex-col justify-between overflow-hidden border border-white/10 bg-[#0d0d0d] p-8 transition-all duration-300 hover:-translate-y-1 hover:border-volt sm:p-10"
            >
              <div className="flex items-start justify-between">
                <span className="border border-volt/40 px-3 py-1 font-body text-[10px] font-bold uppercase tracking-[0.2em] text-volt">
                  {p.tag}
                </span>
                <ArrowUpRight className="h-6 w-6 text-neutral-600 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-volt" />
              </div>

              <div className="mt-8">
                <h3 className="font-heading text-3xl font-extrabold uppercase text-white sm:text-4xl">
                  {p.name}
                </h3>
                <p className="mt-3 max-w-md font-body text-sm leading-relaxed text-neutral-400">
                  {p.desc}
                </p>
                {/* Keyword-rich dofollow anchor for SEO backlink */}
                <span className="mt-6 inline-flex items-center gap-2 font-body text-sm font-bold uppercase tracking-wide text-volt">
                  <ExternalLink className="h-4 w-4" />
                  {p.anchor}
                </span>
              </div>
            </motion.a>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {REGIONAL_PARTNERS.map((r) => (
              <a
                key={r.url}
                href={r.url}
                target="_blank"
                rel="noopener"
                data-testid={`regional-partner-${r.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="group flex items-center justify-between border border-white/10 bg-[#0d0d0d] px-5 py-4 transition-colors hover:border-volt/60"
              >
                <span className="font-body text-sm font-semibold text-neutral-300 group-hover:text-white">{r.anchor}</span>
                <ExternalLink className="h-4 w-4 shrink-0 text-neutral-600 group-hover:text-volt" />
              </a>
            ))}
          </div>
          <p className="mt-6 font-body text-sm text-neutral-500">
            Mehr erfahren:{" "}
            <a href="https://dr-coding.de" target="_blank" rel="noopener" className="font-semibold text-neutral-300 underline decoration-volt/40 underline-offset-4 hover:text-volt">
              Tuning &amp; Files (Dr-Coding)
            </a>{" "}
            ·{" "}
            <a href="https://filesuite.app" target="_blank" rel="noopener" className="font-semibold text-neutral-300 underline decoration-volt/40 underline-offset-4 hover:text-volt">
              Website &amp; Konfigurator (FileSuite)
            </a>
          </p>
        </Reveal>
      </div>
    </section>
  );
}
